import { Component, type ErrorInfo, type ReactNode } from 'react';
import {
  collectErrorData,
  type ErrorBoundaryScope
} from '../../services/bugReport/collectErrorData';
import { sendBugReport } from '../../services/bugReport/bugReportService';
import { ErrorFallback } from './ErrorFallback';

interface AppErrorBoundaryProps {
  children: ReactNode;
  /** Уровень: глобальный (полноэкранный) или пер-страничный (внутри layout). */
  scope: ErrorBoundaryScope;
  /**
   * При изменении любого значения в массиве boundary сбрасывается автоматически.
   * Для пер-страничного — `[location.pathname]`: переход на другой маршрут
   * убирает fallback без явного клика.
   */
  resetKeys?: unknown[];
}

interface AppErrorBoundaryState {
  hasError: boolean;
  /** id отправленного отчёта — показываем пользователю для передачи разработке. */
  reportId: string | null;
}

/**
 * React ErrorBoundary админ-панели.
 *
 * Ловит ошибки рендера / жизненного цикла дочерних компонентов (главный кейс —
 * «белый экран» от поломанных данных с бэка) и:
 *  1) показывает fallback-экран вместо пустой страницы;
 *  2) уведомляет пользователя сообщить команде разработки;
 *  3) один раз отправляет bug-report с пометкой ADMIN_PANEL.
 *
 * НЕ ловит асинхронные ошибки и ошибки вне дерева React — это by design
 * (HTTP-ошибки уже покрыты тостовым слоем technicalError).
 */
export class AppErrorBoundary extends Component<
  AppErrorBoundaryProps,
  AppErrorBoundaryState
> {
  // Защита от повторной отправки при дополнительных рендерах boundary.
  private hasReported = false;

  state: AppErrorBoundaryState = {
    hasError: false,
    reportId: null
  };

  static getDerivedStateFromError(): Partial<AppErrorBoundaryState> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[AppErrorBoundary] Перехвачена ошибка рендера:', error, errorInfo);

    if (this.hasReported) return;
    this.hasReported = true;

    void this.reportError(error, errorInfo);
  }

  componentDidUpdate(prevProps: AppErrorBoundaryProps): void {
    // Авто-сброс при изменении resetKeys (например, смена маршрута).
    if (this.state.hasError && this.didResetKeysChange(prevProps.resetKeys)) {
      this.reset();
    }
  }

  private didResetKeysChange(prevKeys?: unknown[]): boolean {
    const nextKeys = this.props.resetKeys;
    if (prevKeys === undefined || nextKeys === undefined) return false;
    if (prevKeys.length !== nextKeys.length) return true;
    return nextKeys.some((key, index) => !Object.is(key, prevKeys[index]));
  }

  private async reportError(error: Error, errorInfo: ErrorInfo): Promise<void> {
    const reportData = collectErrorData(error, errorInfo, this.props.scope);
    const reportId = await sendBugReport(reportData);
    if (reportId !== null) {
      this.setState({ reportId });
    }
  }

  private reset = (): void => {
    this.hasReported = false;
    this.setState({ hasError: false, reportId: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <ErrorFallback
          scope={this.props.scope}
          reportId={this.state.reportId}
          onReset={this.reset}
        />
      );
    }

    return this.props.children;
  }
}
